import { useState, useEffect } from 'react';
import { useAdminCrud } from '@/hooks/useAdminCrud';
import { AdminHeader, LoadingGrid, EmptyState, FormActions } from '@/components/admin/AdminComponents';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Edit, Trash2 } from 'lucide-react';

const initialForm = {
  title: '', division: '', hero_image: '', summary: '',
  challenge: '', solution: '', result: '', country_region: '', status: 'Active'
};
const divisions = ['Mobility', 'Surveillance', 'Ultrasonic'];

const AdminCaseStudies = () => {
  const { items, loading, isDialogOpen, editingItem, setIsDialogOpen, createItem, updateItem, deleteItem, openCreate, openEdit, closeDialog } = useAdminCrud('case-studies', 'Case study');
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        title: editingItem.title, division: editingItem.division, hero_image: editingItem.hero_image || '',
        summary: editingItem.summary || '', challenge: editingItem.challenge || '', solution: editingItem.solution || '',
        result: editingItem.result || '', country_region: editingItem.country_region || '', status: editingItem.status
      });
    } else {
      setFormData(initialForm);
    }
  }, [editingItem]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = editingItem ? await updateItem(editingItem.id, formData) : await createItem(formData);
    if (success) setFormData(initialForm);
  };

  const update = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));
  const updateSelect = (field) => (v) => setFormData(prev => ({ ...prev, [field]: v }));

  return (
    <div data-testid="admin-case-studies">
      <AdminHeader
        title="Case Studies" subtitle="Manage project case studies" addLabel="Add Case Study"
        isDialogOpen={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { closeDialog(); setFormData(initialForm); } }}
        dialogTitle={editingItem ? 'Edit Case Study' : 'Add Case Study'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title} onChange={update('title')} required />
            </div>
            <div className="space-y-2">
              <Label>Division *</Label>
              <Select value={formData.division} onValueChange={updateSelect('division')}>
                <SelectTrigger><SelectValue placeholder="Select division" /></SelectTrigger>
                <SelectContent>{divisions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Country/Region</Label>
              <Input value={formData.country_region} onChange={update('country_region')} placeholder="Taiwan, Japan, etc." />
            </div>
            <div className="space-y-2">
              <Label>Hero Image URL</Label>
              <Input value={formData.hero_image} onChange={update('hero_image')} placeholder="https://..." />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Summary</Label>
            <Textarea value={formData.summary} onChange={update('summary')} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Challenge</Label>
            <Textarea value={formData.challenge} onChange={update('challenge')} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Solution</Label>
            <Textarea value={formData.solution} onChange={update('solution')} rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Result</Label>
            <Textarea value={formData.result} onChange={update('result')} rows={3} />
          </div>
          <FormActions onCancel={closeDialog} isEditing={!!editingItem} />
        </form>
      </AdminHeader>

      {loading ? <LoadingGrid count={2} cols="grid-cols-1 md:grid-cols-2" /> : items.length === 0 ? (
        <EmptyState Icon={FileText} title="No case studies yet" subtitle="Add case studies to showcase your projects" onAdd={openCreate} addLabel="Add Case Study" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="aspect-video bg-slate-100">
                {item.hero_image ? (
                  <img src={item.hero_image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400"><FileText className="w-12 h-12" /></div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">{item.division}</span>
                  {item.country_region && <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">{item.country_region}</span>}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{item.summary}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(item)}><Edit className="w-3 h-3 mr-1" />Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => deleteItem(item.id)} className="text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCaseStudies;
